import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Alert, Spinner, Badge, Modal } from 'react-bootstrap';
import { 
  FaDownload, 
  FaPlus,
  FaMinus,
  FaExpand, 
  FaMap, 
  FaInfoCircle,
  FaBuilding,
  FaCalendarAlt,
  FaFileAlt,
  FaEye
} from 'react-icons/fa';
import supabase from '../../supabaseClient'; // Ajuste o caminho conforme sua estrutura
import '../../styles/dashboard.css';

const PlantaEvento = ({ eventoId = null }) => {
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [plantas, setPlantas] = useState([]);
  const [plantaAtual, setPlantaAtual] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [eventoSelecionado, setEventoSelecionado] = useState(eventoId);
  const [alert, setAlert] = useState({ show: false, variant: '', message: '' });
  const [showPlantasList, setShowPlantasList] = useState(false);
  
  // Estados para funcionalidade de pan (arrastar)
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [lastPanOffset, setLastPanOffset] = useState({ x: 0, y: 0 });

  // Função para mostrar alertas
  const showAlert = (variant, message) => {
    setAlert({
      show: true,
      variant,
      message
    });
    
    setTimeout(() => {
      setAlert({ show: false, variant: '', message: '' });
    }, 5000);
  };

  // Carregar eventos disponíveis
  const carregarEventos = async () => {
    try {
      const { data, error } = await supabase
        .from('caex_eventos')
        .select('id, nome, status, data_inicio, data_fim, ativo')
        .eq('ativo', true)
        .in('status', ['ativo', 'pendente', 'em_andamento'])
        .order('data_inicio', { ascending: false });

      if (error) throw error;
      
      setEventos(data || []);
      
      // Se não há evento selecionado e há eventos disponíveis, seleciona o primeiro
      if (!eventoSelecionado && data && data.length > 0) {
        setEventoSelecionado(data[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
      showAlert('danger', 'Erro ao carregar eventos: ' + error.message);
    }
  };

  // Carregar plantas do evento selecionado
  const carregarPlantas = async (eventId) => {
    if (!eventId) return;
    
    setLoading(true);
    try {
      const { data: plantasData, error: plantasError } = await supabase
        .from('caex_plantas_evento')
        .select(`
          id,
          titulo,
          descricao,
          arquivo_url,
          arquivo_nome,
          tamanho,
          formato,
          data_upload,
          evento_id,
          ativo,
          created_at,
          updated_at
        `)
        .eq('evento_id', eventId)
        .eq('ativo', true)
        .order('created_at', { ascending: false });

      if (plantasError) throw plantasError;

      // Buscar dados do evento
      const { data: eventoData, error: eventoError } = await supabase
        .from('caex_eventos')
        .select('id, nome, status, data_inicio, data_fim')
        .eq('id', eventId)
        .single();

      if (eventoError) {
        console.warn('Erro ao carregar dados do evento:', eventoError);
      }

      // Processar os dados das plantas
      const plantasFormatadas = plantasData.map(planta => ({
        ...planta,
        evento_nome: eventoData?.nome || 'Evento não encontrado',
        evento_status: eventoData?.status || 'indefinido',
        data_upload: new Date(planta.data_upload).toLocaleDateString('pt-BR')
      }));

      setPlantas(plantasFormatadas);
      
      // Se há plantas, seleciona a primeira como padrão
      if (plantasFormatadas.length > 0) {
        setPlantaAtual(plantasFormatadas[0]);
      } else {
        setPlantaAtual(null);
        showAlert('info', 'Nenhuma planta encontrada para este evento.');
      }

    } catch (error) {
      console.error('Erro ao carregar plantas:', error);
      showAlert('danger', 'Erro ao carregar plantas: ' + error.message);
      setPlantas([]);
      setPlantaAtual(null);
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados quando o componente monta ou evento muda
  useEffect(() => {
    carregarEventos();
  }, []);

  useEffect(() => {
    if (eventoSelecionado) {
      carregarPlantas(eventoSelecionado);
    }
  }, [eventoSelecionado]);

  // Reset pan offset quando zoom muda ou planta muda
  useEffect(() => {
    setPanOffset({ x: 0, y: 0 });
    setLastPanOffset({ x: 0, y: 0 });
  }, [zoom, plantaAtual]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.1, 0.5));
  };

  // Funções para pan (arrastar)
  const handleMouseDown = (e) => {
    if (!plantaAtual || !isImageFile(plantaAtual.formato) || zoom <= 1) return;
    
    setIsPanning(true);
    setPanStart({
      x: e.clientX - panOffset.x,
      y: e.clientY - panOffset.y
    });
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isPanning) return;
    
    const newOffset = {
      x: e.clientX - panStart.x,
      y: e.clientY - panStart.y
    };
    
    setPanOffset(newOffset);
    e.preventDefault();
  };

  const handleMouseUp = () => {
    if (isPanning) {
      setIsPanning(false);
      setLastPanOffset(panOffset);
    }
  };

  // Funções para touch (mobile)
  const handleTouchStart = (e) => {
    if (!plantaAtual || !isImageFile(plantaAtual.formato) || zoom <= 1) return;
    
    const touch = e.touches[0];
    setIsPanning(true);
    setPanStart({
      x: touch.clientX - panOffset.x,
      y: touch.clientY - panOffset.y
    });
    e.preventDefault();
  };

  const handleTouchMove = (e) => {
    if (!isPanning) return;
    
    const touch = e.touches[0];
    const newOffset = {
      x: touch.clientX - panStart.x,
      y: touch.clientY - panStart.y
    };
    
    setPanOffset(newOffset);
    e.preventDefault();
  };

  const handleTouchEnd = () => {
    if (isPanning) {
      setIsPanning(false);
      setLastPanOffset(panOffset);
    }
  };

  const handleFullscreen = () => {
    const element = document.getElementById('planta-container');
    
    if (!isFullscreen) {
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
    
    setIsFullscreen(!isFullscreen);
  };

  // Download da planta atual - versão melhorada
  const handleDownload = async () => {
    if (!plantaAtual) {
      showAlert('warning', 'Nenhuma planta selecionada para download.');
      return;
    }

    setDownloadLoading(true);
    
    try {
      // Buscar o arquivo usando fetch
      const response = await fetch(plantaAtual.arquivo_url);
      
      if (!response.ok) {
        throw new Error(`Erro ao baixar arquivo: ${response.status}`);
      }
      
      // Converter para blob
      const blob = await response.blob();
      
      // Criar URL temporária para o blob
      const url = window.URL.createObjectURL(blob);
      
      // Criar link temporário para download
      const link = document.createElement('a');
      link.href = url;
      link.download = plantaAtual.arquivo_nome || `planta_${plantaAtual.titulo}.${plantaAtual.formato.toLowerCase()}`;
      
      // Adicionar ao DOM, clicar e remover
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpar URL temporária
      window.URL.revokeObjectURL(url);
      
      showAlert('success', 'Download da planta concluído com sucesso!');
      
    } catch (error) {
      console.error('Erro no download:', error);
      showAlert('danger', 'Erro ao fazer download da planta: ' + error.message);
    } finally {
      setDownloadLoading(false);
    }
  };

  // Verificar se o arquivo é uma imagem
  const isImageFile = (formato) => {
    if (!formato) return false;
    const imageFormats = ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'];
    return imageFormats.includes(formato.toUpperCase());
  };

  // Renderizar a planta atual
  const renderPlanta = () => {
    if (!plantaAtual) {
      return (
        <div style={{ 
          height: '400px',
          display: 'flex',
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <FaMap size={64} className="text-muted" />
          <div className="text-center">
            <h5 className="text-muted">Nenhuma planta disponível</h5>
            <p className="text-muted mb-0">
              {eventoSelecionado ? 'Não há plantas cadastradas para este evento.' : 'Selecione um evento para visualizar as plantas.'}
            </p>
          </div>
        </div>
      );
    }

    if (isImageFile(plantaAtual.formato)) {
      return (
        <img 
          src={plantaAtual.arquivo_url}
          alt={plantaAtual.titulo}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            userSelect: 'none',
            pointerEvents: 'none' // Previne seleção da imagem
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
          draggable={false} // Previne o drag nativo da imagem
        />
      );
    } else {
      // Para PDFs e outros arquivos
      return (
        <div style={{ 
          height: '400px',
          display: 'flex',
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <FaFileAlt size={64} className="text-primary" />
          <div className="text-center">
            <h5>{plantaAtual.titulo}</h5>
            <p className="text-muted">Arquivo PDF - {plantaAtual.tamanho}</p>
            <Button 
              variant="primary" 
              onClick={() => window.open(plantaAtual.arquivo_url, '_blank')}
            >
              <FaEye className="me-2" /> Visualizar PDF
            </Button>
          </div>
        </div>
      );
    }
  };

  // Determinar o cursor baseado no estado
  const getCursor = () => {
    if (!plantaAtual || !isImageFile(plantaAtual.formato)) return 'default';
    if (isPanning) return 'grabbing';
    if (zoom > 1) return 'grab';
    return 'default';
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-main-title">
              <FaMap className="dashboard-title-icon" />
              Planta do Evento
            </h1>
            <p className="dashboard-subtitle">
              Visualize e faça download da planta oficial do evento
            </p>
          </div>
        </div>
        
        {/* Alerta de sucesso/erro */}
        {alert.show && (
          <Alert 
            variant={alert.variant} 
            onClose={() => setAlert({ show: false, variant: '', message: '' })} 
            dismissible
            className="mb-4"
          >
            {alert.message}
          </Alert>
        )}

        {/* Seleção de Evento */}
        <Card className="modern-table-card mb-4">
          <Card.Header className="modern-card-header">
            <h5 className="m-0 d-flex align-items-center gap-2">
              <FaCalendarAlt /> Selecionar Evento
            </h5>
          </Card.Header>
          <Card.Body className="modern-card-body">
            <Row>
              <Col md={8}>
                <select 
                  className="form-select"
                  value={eventoSelecionado || ''}
                  onChange={(e) => setEventoSelecionado(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Selecione um evento</option>
                  {eventos.map(evento => (
                    <option key={evento.id} value={evento.id}>
                      {evento.nome} 
                      {evento.status && ` (${evento.status})`}
                      {evento.data_inicio && ` - ${new Date(evento.data_inicio).toLocaleDateString('pt-BR')}`}
                    </option>
                  ))}
                </select>
              </Col>
              <Col md={4}>
                {plantas.length > 1 && (
                  <Button 
                    variant="outline-primary"
                    onClick={() => setShowPlantasList(true)}
                    className="w-100"
                  >
                    Ver todas as plantas ({plantas.length})
                  </Button>
                )}
              </Col>
            </Row>
            
            {plantaAtual && (
              <div className="mt-3 p-3 bg-light rounded">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="mb-1">{plantaAtual.titulo}</h6>
                    <p className="text-muted mb-1">{plantaAtual.descricao}</p>
                    <small className="text-muted">
                      Formato: {plantaAtual.formato} • Tamanho: {plantaAtual.tamanho} • 
                      Upload: {plantaAtual.data_upload}
                    </small>
                  </div>
                  <Badge bg="success">Ativo</Badge>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
        
        {/* Controles da Planta */}
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">
            <FaBuilding className="dashboard-section-icon" />
            Visualização da Planta
          </h2>
          <p className="dashboard-section-subtitle">
            Utilize os controles abaixo para ajustar a visualização
          </p>
        </div>

        <Card className="modern-table-card mb-4">
          <Card.Body className="modern-card-body">
            {/* Loading Spinner */}
            {loading && (
              <div className="text-center mb-3">
                <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                Carregando plantas...
              </div>
            )}

            {/* Controles principais */}
            <div className="mb-3 d-flex flex-wrap gap-2">
              <Button 
                variant="secondary" 
                className="d-flex align-items-center gap-2"
                onClick={handleFullscreen}
                disabled={!plantaAtual || !isImageFile(plantaAtual?.formato)}
              >
                <FaExpand /> {isFullscreen ? 'Sair da Tela Cheia' : 'Tela Cheia'}
              </Button>
              <Button 
                variant="success" 
                className="d-flex align-items-center gap-2"
                onClick={handleDownload}
                disabled={loading || downloadLoading || !plantaAtual}
              >
                <FaDownload /> 
                {downloadLoading ? 'Baixando...' : 'Download da Planta'}
              </Button>
            </div>
            
            {/* Container da planta */}
            <div 
              id="planta-container" 
              className="planta-container border rounded mb-4"
              style={{ 
                position: 'relative',
                height: '500px',
                overflow: 'hidden',
                cursor: getCursor()
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Controles de Zoom Flutuantes - apenas para imagens */}
              {plantaAtual && isImageFile(plantaAtual.formato) && (
                <>
                  <div style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <button
                      onClick={handleZoomIn}
                      disabled={zoom >= 2}
                      style={{
                        width: '40px',
                        height: '40px',
                        border: 'none',
                        backgroundColor: zoom >= 2 ? '#f5f5f5' : '#fff',
                        color: zoom >= 2 ? '#ccc' : '#333',
                        cursor: zoom >= 2 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        transition: 'all 0.2s ease',
                        borderBottom: '1px solid #e0e0e0'
                      }}
                      title="Aumentar zoom"
                    >
                      <FaPlus />
                    </button>
                    
                    <button
                      onClick={handleZoomOut}
                      disabled={zoom <= 0.5}
                      style={{
                        width: '40px',
                        height: '40px',
                        border: 'none',
                        backgroundColor: zoom <= 0.5 ? '#f5f5f5' : '#fff',
                        color: zoom <= 0.5 ? '#ccc' : '#333',
                        cursor: zoom <= 0.5 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        transition: 'all 0.2s ease'
                      }}
                      title="Diminuir zoom"
                    >
                      <FaMinus />
                    </button>
                  </div>

                  {/* Indicador de Zoom */}
                  <div style={{
                    position: 'absolute',
                    bottom: '15px',
                    right: '15px',
                    zIndex: 1000,
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {Math.round(zoom * 100)}%
                  </div>
                </>
              )}

              {/* Área da planta com zoom e pan aplicados */}
              <div style={{ 
                transform: plantaAtual && isImageFile(plantaAtual.formato) 
                  ? `scale(${zoom}) translate(${panOffset.x / zoom}px, ${panOffset.y / zoom}px)` 
                  : 'none',
                transformOrigin: 'center center',
                width: '100%', 
                height: '100%', 
                display: 'flex',
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#f8f9fa',
                transition: isPanning ? 'none' : 'transform 0.3s ease',
                padding: '20px'
              }}>
                {renderPlanta()}
              </div>
            </div>

            {plantaAtual && isImageFile(plantaAtual.formato) && (
              <div className="mb-4">              
                <div className="mt-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                  <small className="text-muted">
                    <strong>💡 Dicas de navegação:</strong><br/>
                    • Use os botões + e - para ajustar o zoom<br/>
                    • {zoom > 1 ? 'Clique e arraste para navegar pela planta ampliada' : 'Aplique zoom para habilitar a navegação por arrasto'}<br/>
                    • Entre em tela cheia para uma melhor experiência de visualização<br/>
                    • Zoom atual: <strong>{Math.round(zoom * 100)}%</strong>
                  </small>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Informações Adicionais */}
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">
            <FaInfoCircle className="dashboard-section-icon" />
            Informações Adicionais
          </h2>
          <p className="dashboard-section-subtitle">
            Detalhes importantes sobre a planta do evento
          </p>
        </div>

        <Card className="modern-table-card mb-4">
          <Card.Body className="modern-card-body">
            <p>
              A planta disponibilizada é a versão oficial do evento. Consulte-a para verificar a localização
              exata do seu estande e planejar a montagem conforme as regras do evento.
            </p>
            <p>
              Se precisar de assistência com a interpretação da planta, entre em contato com a equipe 
              organizadora através do e-mail <strong> caex@mmeventoshd.com.br</strong> ou pelo telefone <strong>(19) 99636-6402</strong>.
            </p>

            <div className="alert alert-info mt-3">
              <strong>Importante:</strong> Verifique sempre as dimensões do seu estande antes de planejar
              a montagem e a disposição dos elementos. As medidas na planta estão em metros.
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Modal para listar todas as plantas */}
      <Modal show={showPlantasList} onHide={() => setShowPlantasList(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Plantas Disponíveis</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            {plantas.map(planta => (
              <div key={planta.id} className="col-md-6 mb-3">
                <Card 
                  className={`h-100 ${plantaAtual?.id === planta.id ? 'border-primary' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setPlantaAtual(planta);
                    setShowPlantasList(false);
                    setZoom(1); // Reset zoom
                    setPanOffset({ x: 0, y: 0 }); // Reset pan
                    setLastPanOffset({ x: 0, y: 0 });
                  }}
                >
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="mb-1">{planta.titulo}</h6>
                      {plantaAtual?.id === planta.id && (
                        <Badge bg="primary">Atual</Badge>
                      )}
                    </div>
                    <p className="text-muted small mb-2">{planta.descricao}</p>
                    <div className="d-flex justify-content-between text-muted small">
                      <span>{planta.formato}</span>
                      <span>{planta.tamanho}</span>
                      <span>{planta.data_upload}</span>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPlantasList(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PlantaEvento;